import React, { forwardRef } from 'react';
import { CONFIG } from '../data/config';
import { CheckoutData, CartItemType } from '../types/types';
import { useLanguage } from '../context/LanguageContext';
interface OrderTicketProps {
  orderNumber: string;
  data: CheckoutData;
  items: CartItemType[];
  cartTotal: number;
  totalFinal: number;
}
export const OrderTicket = forwardRef<HTMLDivElement, OrderTicketProps>(({
  orderNumber,
  data,
  items,
  cartTotal,
  totalFinal
}, ref) => {
  const {
    t,
    language,
    formatPrice,
    translateProductName,
    translateIngredient,
    translateSelectionLabel
  } = useLanguage();
  const cartTotalFormatted = formatPrice(cartTotal);
  const expressFeeFormatted = formatPrice(CONFIG.EXPRESS_FEE);
  const totalFinalFormatted = formatPrice(totalFinal);
  // Use language-aware date formatting
  const dateLocale = language === 'es' ? 'es-CR' : language === 'fr' ? 'fr-FR' : language === 'zh' ? 'zh-CN' : language === 'pt' ? 'pt-BR' : language === 'de' ? 'de-DE' : 'en-US';
  return <div ref={ref} className="bg-surface w-[340px] p-6 font-mono text-sm border border-gray-800 shadow-2xl relative overflow-hidden" data-id="element-1355">
        {/* Ticket zig-zag top edge (simulated with CSS) */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-repeat-x bg-[length:10px_10px] bg-[radial-gradient(circle_at_50%_0,transparent_5px,#1A1A1A_6px)]" data-id="element-1356" />

        {/* Header */}
        <div className="text-center mb-6 pt-2" data-id="element-1357">
          <div className="w-16 h-16 mx-auto mb-3 grayscale contrast-200" data-id="element-1358">
            <img src={CONFIG.LOGO_URL} alt={CONFIG.APP_NAME} className="w-full h-full object-contain rounded-full border border-gray-700" data-id="element-1359" />
          </div>
          <h1 className="font-bold text-xl text-white tracking-widest uppercase" data-id="element-1360">
            {CONFIG.APP_NAME}
          </h1>
          <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider" data-id="element-1361">
            {t('tagline')}
          </p>
          <div className="border-b-2 border-dashed border-gray-700 my-4" data-id="element-1362" />
          <p className="font-bold text-lg text-white" data-id="element-1363">
            {t('orderNumber')} #{orderNumber}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1364">
            {new Date().toLocaleDateString(dateLocale, {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}
          </p>
        </div>

        {/* Customer Info */}
        <div className="mb-6 space-y-1 text-gray-300" data-id="element-1365">
          <p data-id="element-1366">
            <span className="font-semibold text-white" data-id="element-1367">{t('client')}:</span>{' '}
            {data.nombre}
          </p>
          <p data-id="element-1368">
            <span className="font-semibold text-white" data-id="element-1369">{t('tel')}:</span>{' '}
            {data.telefono}
          </p>
          <p data-id="element-1370">
            <span className="font-semibold text-white" data-id="element-1371">{t('delivery')}:</span>{' '}
            {data.entrega === 'Retiro en local' ? t('pickupLocal') : t('express')}
          </p>
          <p data-id="element-1372">
            <span className="font-semibold text-white" data-id="element-1373">{t('payment')}:</span>{' '}
            {data.metodoPago === 'Tarjeta' ? t('card') : data.metodoPago === 'Efectivo' ? t('cash') : data.metodoPago}
          </p>
        </div>

        <div className="border-b-2 border-dashed border-gray-700 mb-4" data-id="element-1374" />

        {/* Items */}
        <div className="mb-6 space-y-4" data-id="element-1375">
          <div className="flex justify-between font-bold text-white mb-2 pb-2 border-b border-gray-800" data-id="element-1376">
            <span data-id="element-1377">
              {t('qty')} {t('description')}
            </span>
            <span data-id="element-1378">{t('total')}</span>
          </div>

          {items.map((item, idx) => <div key={idx} className="text-gray-300" data-id="element-1379">
              <div className="flex justify-between font-bold text-white" data-id="element-1380">
                <span data-id="element-1381">
                  {item.quantity}x{' '}
                  {translateProductName(item.productId, item.name)}
                </span>
                <span data-id="element-1382">
                  {formatPrice(item.subtotal * item.quantity).primary}
                </span>
              </div>

              <div className="pl-4 text-xs mt-1 space-y-0.5 text-gray-400" data-id="element-1383">
                {item.selections.base && <p data-id="element-1384">
                    {translateSelectionLabel('base')}:{' '}
                    {translateIngredient(item.selections.base.id, item.selections.base.name)}
                  </p>}
                {item.selections.proteina && <p data-id="element-1385">
                    {translateSelectionLabel('proteina')}:{' '}
                    {translateIngredient(item.selections.proteina.id, item.selections.proteina.name)}
                  </p>}
                {item.selections.toppings && item.selections.toppings.length > 0 && <p data-id="element-1386">
                      {t('toppings')}:{' '}
                      {item.selections.toppings.map(tp => translateIngredient(tp.id, tp.name)).join(', ')}
                    </p>}
                {item.selections.toppingsPremium && item.selections.toppingsPremium.length > 0 && <p data-id="element-1387">
                      {translateSelectionLabel('premium')}:{' '}
                      {item.selections.toppingsPremium.map(tp => translateIngredient(tp.id, tp.name)).join(', ')}
                    </p>}
                {item.selections.aderezos && item.selections.aderezos.length > 0 && <p data-id="element-1388">
                      {t('dressing')}:{' '}
                      {item.selections.aderezos.map(ad => translateIngredient(ad.id, ad.name)).join(', ')}
                    </p>}
                {item.selections.semillas && item.selections.semillas.length > 0 && <p data-id="element-1389">
                      {translateSelectionLabel('seeds')}:{' '}
                      {item.selections.semillas.map(sd => translateIngredient(sd.id, sd.name)).join(', ')}
                    </p>}
                {item.selections.selectedSize && <p data-id="element-1390">Tamaño: {item.selections.selectedSize.name}</p>}
                {item.selections.halfAndHalf?.enabled && <p data-id="element-1391">Mitad con: {item.selections.halfAndHalf.secondHalfName}</p>}
                {item.selections.comentarios && <p className="italic mt-1" data-id="element-1392">
                    {t('note')}: {item.selections.comentarios}
                  </p>}
              </div>
            </div>)}
        </div>

        <div className="border-b-2 border-dashed border-gray-700 mb-4" data-id="element-1393" />

        {/* Totals */}
        <div className="space-y-1 mb-6 text-gray-300" data-id="element-1394">
          <div className="flex justify-between" data-id="element-1395">
            <span data-id="element-1396">{t('subtotal')}</span>
            <span data-id="element-1397">{cartTotalFormatted.primary}</span>
          </div>
          {data.entrega === 'Express' && <div className="flex justify-between" data-id="element-1398">
              <span data-id="element-1399">{t('express')}</span>
              <span data-id="element-1400">{expressFeeFormatted.primary}</span>
            </div>}
          <div className="flex justify-between font-bold text-lg pt-2 mt-2 border-t border-gray-800 text-white" data-id="element-1401">
            <span data-id="element-1402">{t('total')}</span>
            <span data-id="element-1403">{totalFinalFormatted.primary}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 mt-8" data-id="element-1404">
          <p className="font-bold text-gray-400 mb-1" data-id="element-1405">{t('thankYou')}</p>
          <p data-id="element-1406">{t('followUs')}</p>
          <p data-id="element-1407">@{CONFIG.APP_NAME.replace(/\s+/g, '').toLowerCase()}</p>
        </div>

        {/* Ticket zig-zag bottom edge */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-repeat-x bg-[length:10px_10px] bg-[radial-gradient(circle_at_50%_10px,transparent_5px,#1A1A1A_6px)]" data-id="element-1408" />
      </div>;
});